/**
 * `<caos-spacer>` — deliberate space between two pieces, sized from the platform spacing scale. (Catalog: Layout & containers › Spacer — deliberate space between two pieces, sized from the platform spacing scale.)
 *
 * CAOS-474. A spacer is an ordinary component: it declares a size and a direction, where it may be
 * placed, and that it needs no record. It draws nothing on a running page. All it does is take up its
 * size, so the piece after it starts that much further on.
 *
 * ── THE SIZE IS A STEP OF THE SCALE, NEVER A LENGTH ─────────────────────────
 * `size` names a step of the spacing scale: `x-small` is `--caos-space-1`, and each name after it is the
 * next step, up to `xxx-large` at `--caos-space-7`. `small`, `medium` and `large` are the same steps a
 * layout item's padding uses under those names. A theme that changes the scale moves every spacer with
 * it, and no page can drift off the scale, because there is no number to type. A value that is not a
 * step matches no rule, and the default size applies.
 *
 * ── DIRECTION ────────────────────────────────────────────────────────────────
 * `vertical`, the default, is space between pieces stacked one above the other: the spacer takes its
 * size as height, across the full width. `horizontal` is space between pieces side by side in a
 * layout's row: it takes its size as width, and never shrinks below it.
 *
 * The width is a FLOOR, not a preference. A horizontal spacer sits in its own layout item, and a
 * layout item may shrink, so `min-width` is declared alongside `width` at every step: the gap a page
 * asked for is the gap it keeps, however crowded the row. Give that item `flexibility="no-shrink"`
 * and the column will not squeeze either.
 *
 * ── INVISIBLE ON A PAGE, VISIBLE WHILE AUTHORING ─────────────────────────────
 * On a running page a spacer paints nothing and is hidden from assistive technology. A surface that is
 * authoring the page sets the `authoring` attribute, and the spacer then draws a dashed outline over a
 * tinted fill, so a person can see and select what they placed. `authoring` is deliberately not an
 * input: a stored page cannot set it, so a spacer never shows to somebody reading the page.
 *
 * A horizontal spacer has no height of its own, which on a page is right and while authoring would
 * leave an outline nobody can see or click. So it is given one — and only while authoring, so a page
 * gains no space from it.
 *
 * It aligns nothing. Alignment and distribution belong to `<caos-layout>`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** The spacing-scale steps a spacer may take, smallest first. The nth is `--caos-space-<n>`. */
export const SPACER_SIZES = ['x-small', 'small', 'medium', 'large', 'x-large', 'xx-large', 'xxx-large'] as const;
export type SpacerSize = (typeof SPACER_SIZES)[number];
export const SPACER_DEFAULT_SIZE: SpacerSize = 'medium';

export const SPACER_DIRECTIONS = ['vertical', 'horizontal'] as const;
export const SPACER_DEFAULT_DIRECTION = 'vertical';

/** The spacing token a size step reads. */
export const spacerToken = (size: SpacerSize): string => `--caos-space-${SPACER_SIZES.indexOf(size) + 1}`;

/**
 * The spacer's stylesheet, as written. Exported so a test can read every declaration, including any the
 * test DOM's own parser would drop.
 */
export const SPACER_STYLES = `
  :host {
    display: block;
    flex: 0 0 auto;
    width: 100%;
    height: var(${spacerToken(SPACER_DEFAULT_SIZE)});
  }
  ${SPACER_SIZES.map((size) => `:host([size="${size}"]) { height: var(${spacerToken(size)}); }`).join('\n  ')}

  :host([direction="horizontal"]) {
    display: inline-block;
    width: var(${spacerToken(SPACER_DEFAULT_SIZE)});
    min-width: var(${spacerToken(SPACER_DEFAULT_SIZE)});
    height: auto;
    align-self: stretch;
  }
  ${SPACER_SIZES.map((size) => `:host([direction="horizontal"][size="${size}"]) { width: var(${spacerToken(size)}); min-width: var(${spacerToken(size)}); height: auto; }`).join('\n  ')}

  :host([authoring]) {
    outline: 1px dashed var(--caos-color-accent);
    outline-offset: -1px;
    background: var(--caos-color-accent-soft);
  }

  :host([authoring][direction="horizontal"]) { min-height: var(--caos-space-5); }
`;

const sheet = makeSheet(SPACER_STYLES);

export class CaosSpacer extends CaosElement {
  constructor() {
    super(sheet);
  }

  connectedCallback(): void {
    // Space is not content. A reader of the page hears the pieces on either side of it and nothing else.
    if (!this.hasAttribute('aria-hidden')) this.setAttribute('aria-hidden', 'true');
  }
}

define('caos-spacer', CaosSpacer);

declare global {
  interface HTMLElementTagNameMap {
    'caos-spacer': CaosSpacer;
  }
}
